import { Elements, Link as PrismicLink } from 'prismic-reactjs';
import React from 'react';
import { theme } from '../../config/theme';
import {
    Body1,
    Head1,
    Head2,
    Head3,
    Head4,
    Head5,
    Head6,
} from '../components/Typography/Typography';
import Link from '../components/Link/Link';
import linkResolver from './linkResolver';
import { rem } from 'polished';
import { css } from 'styled-components';
import Image from '../components/Image/Image';
import Box from '../components/Box/Box';
import { generateHeadingId } from './generateHeadingId';

const MediaWrapper = ({ children, ...props }) => (
    <Box
        my={rem(8)}
        p={rem(16)}
        borderRadius="sm"
        boxShadow="primary-thin"
        css={css`
            img {
                border-radius: ${({ theme }) => theme.radii.sm};
                margin-inline: auto;
            }
        `}
        {...props}
    >
        {children}
    </Box>
);

const htmlSerializer = function (type, element, content, children, key) {
    switch (type) {
        case Elements.heading1:
            return React.createElement(
                Head1,
                {
                    key,
                    mb: rem('28px'),
                    id: generateHeadingId(
                        element.text.replace(/(\r\n|\n|\r)/gm, '')
                    ),
                    style: {
                        scrollMarginTop: 'calc(var(--header-height) + 20px)',
                    },
                },
                children
            );
        case Elements.heading2:
            return React.createElement(
                Head2,
                {
                    key,
                    mb: rem('24px'),
                    id: generateHeadingId(
                        element.text.replace(/(\r\n|\n|\r)/gm, '')
                    ),
                    style: {
                        scrollMarginTop: 'calc(var(--header-height) + 20px)',
                    },
                },
                children
            );
        case Elements.heading3:
            return React.createElement(
                Head3,
                {
                    key,
                    mb: rem('20px'),
                    id: generateHeadingId(
                        element.text.replace(/(\r\n|\n|\r)/gm, '')
                    ),
                    style: {
                        scrollMarginTop: 'calc(var(--header-height) + 20px)',
                    },
                },
                children
            );
        case Elements.heading4:
            return React.createElement(
                Head4,
                {
                    key,
                    mb: rem('16px'),
                    id: generateHeadingId(
                        element.text.replace(/(\r\n|\n|\r)/gm, '')
                    ),
                    style: {
                        scrollMarginTop: 'calc(var(--header-height) + 20px)',
                    },
                },
                children
            );
        case Elements.heading5:
            return React.createElement(
                Head5,
                {
                    key,
                    mb: rem('12px'),
                    id: generateHeadingId(
                        element.text.replace(/(\r\n|\n|\r)/gm, '')
                    ),
                    style: {
                        scrollMarginTop: 'calc(var(--header-height) + 20px)',
                    },
                },
                children
            );
        case Elements.heading6:
            return React.createElement(
                Head6,
                {
                    key,
                    mb: rem('12px'),
                    id: generateHeadingId(
                        element.text.replace(/(\r\n|\n|\r)/gm, '')
                    ),
                    style: {
                        scrollMarginTop: 'calc(var(--header-height) + 20px)',
                    },
                },
                children
            );
        case Elements.paragraph:
            const isEmpty = !Boolean(element.text.trim());
            return (
                <Body1
                    key={key}
                    css={css`
                        + *:not(:empty) {
                            margin-top: ${rem(20)};
                        }
                    `}
                    {...(isEmpty && { height: rem(20) })}
                >
                    {isEmpty ? null : children}
                </Body1>
            );
        case Elements.list:
            return (
                <Box
                    as="ul"
                    key={key}
                    listStyle="disc"
                    ml={rem(30)}
                    css={css`
                        + *:not(:empty) {
                            margin-top: ${rem(20)};
                        }
                    `}
                >
                    {children}
                </Box>
            );
        case Elements.listItem:
            return (
                <Body1 key={key} as="li">
                    {children}
                </Body1>
            );
        case Elements.strong:
            return React.createElement(
                'strong',
                {
                    key,
                    style: {
                        fontWeight: 'bold',
                    },
                },
                children
            );
        case Elements.image: // Image
            return (
                <MediaWrapper>
                    <Image
                        img={{
                            url: element.url,
                            alt: element.alt || '',
                        }}
                        width="auto"
                    />
                </MediaWrapper>
            );
        case Elements.embed: // Embed
            const embedHtml = React.createElement('div', {
                dangerouslySetInnerHTML: { __html: element.oembed.html },
            });

            return (
                <MediaWrapper
                    css={css`
                        iframe {
                            height: 100%;
                            aspect-ratio: 16 / 9;
                            border-radius: ${({ theme }) => theme.radii.sm};
                            overflow: hidden;
                        }
                    `}
                >
                    {embedHtml}
                </MediaWrapper>
            );

        case Elements.hyperlink:
            const url = PrismicLink.url(element.data, linkResolver);

            if (element.data.link_type === 'Document') {
                return (
                    <Link to={url} key={key}>
                        {content}
                    </Link>
                );
            }
            const target = element.data.target
                ? { target: element.data.target, rel: 'noopener' }
                : {};
            return (
                <Link key={key} href={url} colorScheme="primary" {...target}>
                    {children}
                </Link>
            );
        case Elements.label:
            if (element.data.label === 'accent')
                return React.createElement(
                    'span',
                    {
                        key,
                        style: {
                            color: theme.colors.primary.main,
                        },
                    },
                    children
                );
            if (element.data.label === 'light') {
                return React.createElement(
                    'span',
                    {
                        key,
                        style: {
                            color: theme.colors.white,
                        },
                    },
                    children
                );
            }
            if (element.data.label === 'underline') {
                return (
                    <span style={{ textDecoration: 'underline' }} key={key}>
                        {children}
                    </span>
                );
            }
            if (element.data.label === 'align-left') {
                return (
                    <div style={{ textAlign: 'left' }} key={key}>
                        {children}
                    </div>
                );
            }
            if (element.data.label === 'align-right') {
                return (
                    <div style={{ textAlign: 'right' }} key={key}>
                        {children}
                    </div>
                );
            }

            break;
        // case Elements.span:
        //     return React.createElement(Caption, { key }, children);

        default:
            return null;
    }
};

export const plainHtmlSerializer = function (
    type,
    element,
    content,
    children,
    key
) {
    switch (type) {
        case Elements.hyperlink:
            const url = PrismicLink.url(element.data, linkResolver);

            if (element.data.link_type === 'Document') {
                return (
                    <Link to={url} key={key}>
                        {content}
                    </Link>
                );
            }
            const target = element.data.target
                ? { target: element.data.target, rel: 'noopener' }
                : {};
            return (
                <a key={key} href={url} {...target}>
                    {children}
                </a>
            );
        case Elements.label:
            if (element.data.label === 'accent')
                return React.createElement(
                    'span',
                    {
                        key,
                        style: {
                            color: theme.colors.primary.main,
                        },
                    },
                    children
                );
            if (element.data.label === 'light') {
                return React.createElement(
                    'span',
                    {
                        key,
                        style: {
                            color: theme.colors.white,
                        },
                    },
                    children
                );
            }
            return <span key={key}>{content || children}</span>;
        case Elements.paragraph:
            return (
                <span style={{ display: 'block' }} key={key}>
                    {content || children}
                </span>
            );
        case Elements.strong:
            return React.createElement(
                'strong',
                {
                    key,
                    style: {
                        fontWeight: 'bold',
                    },
                },
                children
            );
        default:
            return <span key={key}>{content || children}</span>;
    }
};

export default htmlSerializer;
